import type { ReactNode } from 'react'
import photos from '@/content/photos.json'
import SiteLogo from '@/components/Logo'
import SitePhoto from '@/components/Photo'
import {
  TESTIMONIAL_RIBBON,
  testimonialStoryHref,
  type Testimonial,
} from '@/content/testimonials'
import { localizeHref } from '@/i18n/href'

/**
 * The Hall of Fame card — the template the testimonials page is made of.
 *
 * Asked for on 2026-08-27 with ten screenshots of the live design and the page's
 * full text: *"bu tasarım şeklini bizim hall of fame template i olarak
 * kaydedebilirsin"*. So it is a template rather than a page: one exported card,
 * driven by one entry in `content/testimonials.ts`, and adding a twentieth
 * customer is an entry in that file and nothing here.
 *
 * The card, reading down the reference:
 *
 *   - the company in brand blue, small, uppercase and tracked, with a short rule
 *     under it — an eyebrow, the same one `pages/Stories.tsx` and
 *     `pages/Reports.tsx` put above their headlines, so the page belongs to the
 *     same site as the other three indexes;
 *   - a gap, then the mark, the quote, the attribution in bold, and the pill;
 *   - the gradient arc rising out of the bottom of the card behind all of it.
 *
 * Everything sits in a column down the left half. That is not a stylistic
 * preference: the reference gives the right half to the person's photograph, and
 * a quote set across the full width of a 1320px container is an unreadable
 * measure anyway. The column is capped rather than set as a fraction, so the
 * line length is the same on a laptop and on a 27-inch screen.
 *
 * **Static markup only, no state.** These documents are prerendered and never
 * hydrated (see `src/legacy.ts`), so anything needing JavaScript would render
 * once and sit there dead.
 */

type PhotoManifest = Record<
  string,
  { width: number; height: number; webp: { w: number; src: string }[]; fallback: string }
>

/**
 * The arc, resolved once at module load rather than per card.
 *
 * Read out of the manifest the same way `app/Layout.tsx` reads the two ribbons
 * it paints, and for the same reason: the derived filenames carry a width
 * (`gradient-ribbon-698.webp`) that `pnpm images` decides, so spelling one into
 * the markup makes a rebuild able to break the page quietly.
 */
const ribbon = (photos as PhotoManifest)[TESTIMONIAL_RIBBON]

/**
 * The arc behind one card.
 *
 * `isolate` on the card plus `-z-10` here is the pairing `app/Layout.tsx`'s
 * footer uses: inside a stacking context, a negative-z child paints above that
 * element's own background and below its in-flow content — so the ribbon sits on
 * the card's grey and under the quote without either needing a z-index.
 *
 * **Not mirrored, and that is the change of 2026-08-27.** The card used to
 * reuse the site's blue-to-pink `gradient-ribbon.png` flipped; the ribbon meant
 * for this template was then supplied as its own file. It is drawn facing the
 * right way already — the pale end fades out at the bottom-left, toward the
 * quote, and the solid end falls down the card's right edge behind where the
 * portrait goes. Flipping it would put the strong colour beside the words.
 *
 * Sized by height against the card, not by width, because the cards are
 * different heights — Kordsa's quote is three paragraphs and Kuveyt Türk's is
 * three lines — and a fixed-width arc would peak halfway up a short card and
 * a fifth of the way up a tall one.
 *
 * **The height is capped as well as proportional, and that is what keeps the
 * arc out of the words.** Width follows height here (`w-auto` on a replaced
 * element with a fixed height), so the tallest card — ebebek's four
 * paragraphs — would otherwise draw an arc half again as wide as Kuveyt
 * Türk's and reach back into the text column. `max-h` clamps the height, the
 * width recomputes from it, and every arc is the same size whatever the card
 * around it does.
 *
 * **Both numbers are set by the arc's proportion to the person, not by the
 * card** (2026-08-28 — *"kişi ve ribbon oranını bu şekilde yapma imkanımız var
 * mı hall of fame görsellerinde"*, with a reference of the two layers stacked at
 * the proportion wanted). In that reference the arc's own ink is 0.71 of the
 * figure's height and the peak lands about at the chin; at the 320px cap this
 * card drew 0.53 of it — a hump parked beside a person, rather than the frame
 * behind one. The two files are mostly transparent padding, which is where the
 * arithmetic lives and why the on-screen sizes do not look like the ratio:
 * the arc's ink is 0.66 of `gradient-ribbon-pink-arc.png`'s height and a
 * portrait's figure is ~0.88 of its 900×900 square, so at `max-h-[26rem]`
 * (416px) the ink is 273px against the figure's 394px — 0.69, the reference's
 * proportion within a pixel or two of what this arc's shape allows.
 *
 * **416px is a clearance ceiling, not a preference — do not round it up to
 * 430.** Width follows height at 1.25, so 416px draws 520px, and the narrowest
 * window where this arc is drawn at all (1280px, the `xl` breakpoint: a 1160px
 * container, less 96px of card padding, less the 34rem text column, plus the
 * card's own right padding) leaves 568px between the card's right border and
 * the words. 520px keeps ~48px of that clear, and ~86px to the ink. The 430px
 * the first version used draws 537px and spends the lot.
 *
 * The percentage rises with the cap for the same reason — 95% of a card rather
 * than 82% — so the two or three cards short enough to fall under the cap
 * (Epic Museum's four-line quote is the shortest of the nineteen) keep the
 * proportion instead of dropping back to the old one. Both layers clamp to the
 * card, so both shrink together and the ratio holds.
 *
 * **Both ends of the arc land on the card's own edges, not in mid-air** — asked
 * for on 2026-08-27 ("ribbon gri dikdörtgen kutucuğun tam sınırından çıkmalı,
 * bitişi tam kutucuğun sınırlarında olmalı"), and it is the same correction
 * `components/ContactBand.tsx` took the day before. The source draws one
 * continuous curve whose two feet both run off its bottom edge, so `bottom-0`
 * already put them on the card's bottom border; what floated was the descending
 * right leg, which the `right-16` inset held 4rem short of the right border so
 * the arc read as a hump parked near the corner rather than as a ribbon running
 * out of it. At `right-0` the foot lands in the bottom-right corner and the
 * card's `overflow-hidden` trims it along the `rounded-3xl` curve, which is what
 * "ends at the boundary" looks like on a rounded card.
 *
 * Hidden below `xl`, which is wider than it looks like it should be. The text
 * column is 34rem and the arc is ~520px: at `lg` those two do not both fit
 * beside each other in a 1024px window, and an arc drawn across the quote is
 * worse than no arc.
 */
function Ribbon() {
  if (!ribbon) return null
  return (
    <img
      aria-hidden
      alt=""
      src={ribbon.fallback}
      srcSet={ribbon.webp.map((v) => `${v.src} ${v.w}w`).join(', ')}
      sizes="520px"
      width={ribbon.width}
      height={ribbon.height}
      loading="lazy"
      decoding="async"
      className="pointer-events-none absolute bottom-0 right-0 -z-10 hidden h-[95%]
                 max-h-[26rem] w-auto max-w-none xl:block"
    />
  )
}

/**
 * The person who said it, standing on the bottom edge of the card.
 *
 * The right-hand side of this card was designed around a photograph and stood
 * empty from the day the template shipped until 2026-08-27, when ten portraits
 * were sent — *"hall of fame için görseller"* — and the remaining nine followed
 * the same day. All nineteen cards carry one now. The optional branch stays: a
 * card with no portrait draws nothing at all rather than a placeholder, which is
 * what every card looked like the day before and does not read as broken.
 *
 * **No disc, no ring, no fill — and that is the correction of 2026-08-27**
 * (*"hall of fame görsellerini transparent atmam rağmen neden beyaz arka plan
 * attın; gri kutunun tam altından denk gelecek şekilde çıksın insanlar ve
 * transparan olsunlar"*). The first version wrapped each face in a circle filled
 * with `bg-background`, on the reasoning that the crops were cut out against
 * white and a deliberate white disc beat a ghost of studio backdrop. The files
 * are cut out with an alpha channel, so that reasoning bought a white coin on a
 * grey card and hid the thing it was protecting. The picture is now drawn
 * straight onto the card: whatever is transparent in the file shows the card's
 * own grey and the arc behind it.
 *
 * **`bottom-0`, so the figure rises out of the card's bottom border** rather
 * than floating an inch above it — the same correction the arc took an hour
 * earlier, and the same one `components/ContactBand.tsx` took the day before.
 * `object-bottom` keeps the shoulders on that border whatever the file's shape,
 * and the card's own `overflow-hidden` trims anything past it along the
 * `rounded-3xl` curve.
 *
 * **`max-h-full` is what keeps that trim off the face**, and it starts to matter
 * at 384px and more at 448px. The shortest cards on the page are the ones with
 * no story pill and a four-line quote — Epic Museum's is the shortest of the
 * nineteen, and it has no
 * mark either — and a card can come out shorter than the figure standing in it.
 * `overflow-hidden` then cuts the top of somebody's head, at the bottom of the
 * card is where it is anchored. Clamping the height instead makes the picture
 * shrink to whatever the card gives it, still standing on the bottom border. It
 * is the same guard the arc above carries and for the same reason: these cards
 * are nineteen different heights and a fixed figure only fits the tall ones.
 *
 * **448px, and that number is the source files' own ceiling** (2026-08-28 —
 * *"hall of fame görselleri 900 x 900"*, twice in one day). The template shipped
 * at 288px because every file was a 300×300 cut-out and `pnpm images` never
 * upscales: the full-height figure the reference screenshots show would have
 * been a 300px file stretched over 500px of card, and a blurred person is worse
 * than a sharp smaller one. Ten came back at 900×900 that morning and it went to
 * 384px; **the remaining nine — Erdinç Uğurlu, Fatih Akar, Aydın Şener, Barış
 * Şimşek, Şerafettin Özer, Tarkan Ateşoğlu, Mehmet Ali Gülşen, Asli de Munnik
 * and Arif Emre Unal — arrived the same afternoon**, so all nineteen are 900×900
 * and every card can draw the same box at full sharpness. `content/photos.json`
 * carries the width of each and is the list that settles it.
 *
 * It stops at 448px rather than going further because 448 × 2 = 896, which is
 * the 900px source with four pixels to spare. Every card draws the same size
 * box, so the size the template can use is the size the *smallest* source
 * carries — that was the constraint when nine files were 300px and it is still
 * the constraint now, just a much higher one. **The next step up needs bigger
 * originals, not a bigger number here**; at 512px a 2× screen is asking for
 * 1024px of person and would get 900 stretched.
 *
 * `alt` is the name alone. The role is on the line beside the picture already.
 */
function Portrait({
  portrait,
  href,
}: {
  portrait: NonNullable<Testimonial['portrait']>
  /**
   * The story this card points at, if it points at one. Asked for on 2026-09-01
   * — *"hem logo hem şirket ismi hem kişi görseli … tıklanabilir ve ilgili
   * sayfasına yönlendirir olsun"* — so the picture is a link to the same place
   * the pill goes, and looks exactly as it did.
   *
   * The box moves off the picture and onto the wrapper, and the picture fills
   * it (`size-full`): a bare `max-h-full` inside an auto-height anchor resolves
   * against nothing, so the clamp that keeps a tall figure out of a short
   * card's `overflow-hidden` has to stay on the element that is positioned
   * against the card.
   *
   * `aria-hidden` and off the tab order, because the pill below already carries
   * this destination in words — three tab stops and three announcements of the
   * same link is noise, not access.
   */
  href?: string
}) {
  const box = 'absolute bottom-0 right-12 hidden size-[28rem] max-h-full xl:block'
  const picture = (
    <SitePhoto photo={portrait} sizes="448px" className="size-full object-contain object-bottom" />
  )
  return href ? (
    <a href={href} aria-hidden tabIndex={-1} className={box}>
      {picture}
    </a>
  ) : (
    <span className={`pointer-events-none ${box}`}>{picture}</span>
  )
}

/**
 * The same face, small, beside the attribution — what narrow screens get instead
 * of the standing figure, because below `xl` the text column owns the width and
 * a person standing in it would be standing on the quote.
 *
 * Circular here, and that is not a leftover: at 64px beside a name this is an
 * avatar, and an unmasked head-and-shoulders crop at that size reads as a
 * cropping accident. The mask is all that is left of the old disc — no fill and
 * no ring, so the transparent part of the file is card, exactly as on the big
 * one.
 */
function PortraitAvatar({
  portrait,
  href,
}: {
  portrait: NonNullable<Testimonial['portrait']>
  /** Same link as the standing figure, on the screens that get the avatar instead. */
  href?: string
}) {
  const mask = 'block size-16 shrink-0 overflow-hidden rounded-full xl:hidden'
  const picture = <SitePhoto photo={portrait} sizes="64px" className="size-full object-cover" />
  return href ? (
    <a href={href} aria-hidden tabIndex={-1} className={mask}>
      {picture}
    </a>
  ) : (
    <span className={mask}>{picture}</span>
  )
}

/**
 * The company's name at the top of the card — the eyebrow, and now the card's
 * named link to that company's story.
 *
 * **Blue on hover, and nothing else**, settled on 2026-09-01. It was first asked
 * for as *"normalde mavi ve altı çizgili olmasın"* — body colour at rest, blue
 * with a short rule under it on hover — and then, the same day, the rule was
 * asked out of it too: *"mouse over yapınca bu küçük mavi çizginin çıkmasına
 * gerek yok, sadece mavi olmak ve ufak çaplı değişimler yeterli"*. So the only
 * thing that changes under the pointer is the colour. There is no rule left to
 * reserve space for, which is also why the old `opacity-0` trick is gone.
 *
 * This is the one part of the card that is a real link — focusable, in the
 * accessibility tree, and reading as the company's name — while the mark and the
 * portrait beside it are the same destination made clickable and nothing more.
 *
 * No JavaScript in any of it. These documents are prerendered and never hydrate,
 * so hover has to be CSS or it is nothing.
 */
function CompanyEyebrow({ company, href }: { company: string; href?: string }) {
  const name = 'text-sm font-bold uppercase tracking-[0.14em]'
  // Company names are Latin brand spelling, not Turkish words: `uppercase`
  // follows the document's language, and on a `lang="tr"` page that maps
  // `i` to `İ` — "LC Waikiki" came out "LC WAİKİKİ". `lang="en"` on the
  // element pins the case-folding rule regardless of the page it sits on
  // (see the same fix in `sections/tool.tsx`).
  // **A card with no story reads as plain text, not as a link that does
  // nothing** (2026-09-01 — *"tıklamalı olmayan tasarımlar mavi değil düz siyah
  // olsun, mouse over olunca herhangi bir şey olmayınca biz anlayalım sayfası
  // olmadığını"*). It used to print the name in brand blue whether or not there
  // was anywhere to go, which is the same colour the linked cards take under the
  // pointer — so the only way to find out a card had no story was to hover it
  // and watch nothing happen. Body colour at rest and no hover at all makes the
  // difference visible before the pointer arrives.
  if (!href) {
    return <p lang="en" className={`${name} text-foreground`}>{company}</p>
  }
  return (
    <a href={href} className="group inline-block no-underline">
      <span
        lang="en"
        className={`${name} block text-foreground transition-colors
                    group-hover:text-primary group-focus-visible:text-primary`}
      >
        {company}
      </span>
    </a>
  )
}

/**
 * One customer's quote, in the Hall of Fame template.
 *
 * The mark is drawn `fit="quote"` — **one width for all nineteen**, whatever the
 * file's shape (see `components/Logo.tsx`). Not the story card's `plate`, which
 * fills a square tile and there is no tile here; and no longer the wall's
 * height cap, which was the original and was wrong for this page. Asked for on
 * 2026-08-27: *"hall of fame deki şirket logolarının büyüklüğünü eşit yapar
 * mısın bütünlüklü ve tutarlı olsun"*.
 *
 * The wall's cap is right in a row of 55 marks seen at once and wrong in a
 * column of cards seen one at a time: sixteen of the nineteen files are wide
 * wordmarks, and half of those arrived on a padded 300x300 canvas, so `max-h-14`
 * printed the padded ones about 50px of ink across against 150 for a tight crop
 * of the same kind of wordmark. Two cards apart, that reads as two different
 * designs rather than as equal weight.
 *
 * The slot is `h-20` and fixed, so the logo occupies the same band on a card
 * whose mark is a thin wordmark as on one whose mark is deep. A padded square
 * drawn 140px wide overhangs that slot — the overhang is transparent canvas, the
 * ink is not more than half of any of these files, and the alternative (capping
 * the height) shrinks the ink straight back to the problem above.
 *
 * The pill is the site's own `btn-primary` — solid brand blue — one size down
 * from the default so it reads as an action inside a card rather than as the
 * page's main call to action; the contact band at the foot of every page owns
 * that. The reference draws it pink; there is no pink button in the Impact
 * Platform design system and inventing one here would be drift in `index.css`
 * for one page. It shipped as the pale-tint `btn-secondary` for that reason and
 * was made solid blue on 2026-08-28 (*"hall of fame tasarımlarındaki butonlar
 * mavi olsun"*) — the tint sat too close to the card's own grey to read as a
 * button at all, and blue is what the hero band above the cards already uses.
 */
export function TestimonialCard({
  testimonial,
  locale,
  storyLink = true,
  header,
  cta,
  tabbable = true,
}: {
  testimonial: Testimonial
  locale: string
  /**
   * The pill's link and label, supplied by the caller instead of resolved from
   * the Hall of Fame catalogue. `quotes`' carousel passes each item's own `cta`,
   * so a page that carries its quotes as copy — the DAIMI page's three tabs,
   * asked for on 2026-09-01 — can send the reader to that company's story
   * without the card having to know which catalogue entry the quote came from.
   * An English path, like every href in the dictionary; localised here.
   */
  cta?: { label: string; href: string }
  /** False for a hidden carousel pane, so its pill is off the tab order. */
  tabbable?: boolean
  /**
   * Draw the "Read the full story" pill. Off on the story page itself, which
   * reuses this card for its closing quote (2026-08-27) and where the pill
   * would link the reader to the page they are already reading.
   */
  storyLink?: boolean
  /**
   * Something to draw in the eyebrow's place, at the top of the card.
   *
   * Only the partner page's `QuoteCarousel` passes it, and it passes the tab
   * strip — asked for on 2026-08-28: *"seçenekler gri kutucuğun içinden olsun,
   * şirketin isminin hem kutu içinde hem kutu dışında olması farklı
   * gözüküyor"*. With the tabs above the card the active company was written
   * twice, three lines apart, in the same uppercase blue. So the control moves
   * into the slot the name occupied and the **active tab becomes the eyebrow**
   * — same size, same colour, same rule under it — rather than the two of them
   * sitting one above the other.
   *
   * A node and not a `company={false}` flag, because the slot has to keep its
   * own spacing: whatever goes in it is followed by the same `mt-10` gap to the
   * mark, so a card with tabs and a card with a name are the same card. The
   * nineteen Hall of Fame cards and the story page pass nothing and are
   * untouched.
   */
  header?: ReactNode
}) {
  const href = cta
    ? localizeHref(cta.href, locale)
    : storyLink
      ? testimonialStoryHref(locale, testimonial)
      : undefined
  return (
    <article className="relative isolate overflow-hidden rounded-3xl bg-band px-7 py-9 sm:px-12 sm:py-14">
      <Ribbon />
      {/*
        Standing in front of the arc, on the half of the card the quote leaves
        empty — on the bottom border, inset from the right by the card's own
        padding so it lines up with the words. Above `xl` only, the same
        breakpoint the arc uses and for the same reason: below it the text column
        already takes the width, and a portrait there would sit on the quote.
        Smaller screens get the avatar beside the attribution instead, which is
        where a face belongs when there is no room for it to stand on its own.
      */}
      {testimonial.portrait && <Portrait portrait={testimonial.portrait} href={href} />}
      {header ?? <CompanyEyebrow company={testimonial.company} href={href} />}

      <div className="mt-10 max-w-[34rem] sm:mt-16">
        {testimonial.logo && (
          <p className="flex h-20 items-center">
            {href ? (
              // Same destination as the pill, drawn exactly as before: an
              // `inline-flex` wrapper takes the mark's own box and adds nothing
              // to it. Out of the tab order and the accessibility tree for the
              // same reason as the portrait — the pill says it in words.
              <a href={href} aria-hidden tabIndex={-1} className="inline-flex">
                <SiteLogo logo={testimonial.logo} fit="quote" />
              </a>
            ) : (
              <SiteLogo logo={testimonial.logo} fit="quote" />
            )}
          </p>
        )}
        {/*
          `blockquote` rather than a run of paragraphs, because that is what
          these are — and it is what puts the customer's words inside a
          quotation in the document outline rather than leaving them as the
          site's own prose. No quotation marks are drawn: the card, the mark and
          the attribution already say whose words these are, and the reference
          sets them plain.
        */}
        <blockquote className={testimonial.logo ? 'mt-6' : undefined}>
          {testimonial.quote.map((paragraph, i) => (
            <p key={i} className={`leading-relaxed${i ? ' mt-4' : ''}`}>
              {paragraph}
            </p>
          ))}
        </blockquote>
        <div className="mt-6 flex items-center gap-4">
          {testimonial.portrait && (
            <PortraitAvatar portrait={testimonial.portrait} href={href} />
          )}
          <p className="font-bold">{testimonial.attribution}</p>
        </div>
        {href && (
          <p className="mt-8">
            <a
              href={href}
              tabIndex={tabbable ? undefined : -1}
              className="btn-primary px-8 py-3 text-base"
            >
              {cta?.label ?? 'Read the full story'}
            </a>
          </p>
        )}
      </div>
    </article>
  )
}

/**
 * The Hall of Fame: every quote, one card each, stacked.
 *
 * A stack and not a grid, unlike the three index pages next door. A story card
 * is a mark and a name and tiles four across; a testimonial is up to four
 * paragraphs, and two of those side by side is a page nobody finishes. The
 * reference is a single column and it is right.
 */
export function TestimonialCards({
  testimonials,
  locale,
}: {
  testimonials: Testimonial[]
  locale: string
}) {
  return (
    <div className="mt-12 space-y-8">
      {testimonials.map((testimonial) => (
        <TestimonialCard
          key={testimonial.attribution}
          testimonial={testimonial}
          locale={locale}
        />
      ))}
    </div>
  )
}
